/**
 * Entry point.
 *
 * The React Query defaults here are a risk posture, not a performance
 * tweak. `refetchOnWindowFocus` is ON — coming back to a tab that has been
 * open since yesterday must not show yesterday's positions. `retry` is
 * conservative and never retries a 4xx: a `limit_breached` or `kill_switch`
 * response is the platform refusing on purpose, and hammering it is both
 * pointless and misleading.
 */

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { ApiError } from './api/client';
import App from './App';
import { toast } from './components/ui/Toast';
import { primeDemoNames, startDemoScreenText } from './lib/demoNames';
import './index.css';
import { ThemeProvider } from './theme/ThemeProvider';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only shout about a *background* refetch failure. An initial load
      // failure is rendered inline by the page's own ErrorState, and a toast
      // on top of that is noise.
      if (query.state.data === undefined) return;
      if (error instanceof ApiError && error.code === 'aborted') return;
      // A 401 means the session ended (sign-out or expiry). The auth gate
      // handles that by dropping to the login screen; a toast per in-flight
      // query on the way out is just noise.
      if (error instanceof ApiError && (error.status === 401 || error.code === 'unauthorized')) {
        return;
      }
      toast.error(error, 'Background refresh failed');
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // A refusal is an answer. Do not retry it.
          if (!error.isRetryable) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
    },
    mutations: {
      // Never retry a mutation. Every mutation in this app either places an
      // order, changes a risk limit or writes an audit row, and a silent
      // second attempt is how one halt becomes two audit entries — or worse.
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('#root is missing from index.html');
}

// Demo display names (lib/demoNames): the environment is read before the first
// render, so the names are right from the first paint and never on under LIVE.
function render(root: HTMLElement): void {
  createRoot(root).render(
    <StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
  startDemoScreenText();
}

void primeDemoNames()
  .catch(() => false)
  .then((reloading) => {
    if (!reloading) render(container);
  });
